import { useEffect } from "react";

// Absolute origin used for canonical / og:url.
// In production this resolves to https://mimesgourmet.es; in preview to the preview URL.
const SITE_ORIGIN =
  (typeof window !== "undefined" && window.location.origin) || "https://mimesgourmet.es";

const CANONICAL_ORIGIN = "https://mimesgourmet.es";

function toAbsolute(url) {
  if (!url) return `${CANONICAL_ORIGIN}/products/frutos-rojos.webp`;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/")) return `${CANONICAL_ORIGIN}${url}`;
  return `${CANONICAL_ORIGIN}/${url}`;
}

function setMetaByName(name, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setMetaByProperty(property, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function removeCanonical() {
  const el = document.head.querySelector('link[rel="canonical"]');
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

const JSONLD_MARKER = "data-seo-jsonld";

function clearPageJsonLd() {
  const nodes = document.head.querySelectorAll(`script[${JSONLD_MARKER}="page"]`);
  nodes.forEach((n) => n.parentNode && n.parentNode.removeChild(n));
}

function injectJsonLd(blocks) {
  clearPageJsonLd();
  (blocks || []).forEach((block) => {
    if (!block) return;
    try {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute(JSONLD_MARKER, "page");
      s.text = JSON.stringify(block);
      document.head.appendChild(s);
    } catch (e) {
      /* ignore malformed block */
    }
  });
}

/**
 * SEO — sets per-page canonical, meta description, OpenGraph, Twitter,
 * and optional JSON-LD structured data. Compatible with Emergent's crawler
 * pre-render (mutations happen in useEffect, before HTML is captured).
 *
 * Props:
 *  - title         (string) full <title> ("Producto · Mimes Gourmet")
 *  - description   (string) meta description (~150-160 chars)
 *  - path          (string) canonical path starting with "/", e.g. "/producto/fresa-silvestre"
 *  - image         (string) absolute or root-relative image URL for social sharing
 *  - type          (string) OpenGraph type ("website" | "article" | "product")
 *  - jsonLd        (array of objects) JSON-LD blocks to inject on this page
 *  - noindex       (bool)  When true, sets robots noindex,nofollow AND removes
 *                          canonical/JSON-LD to prevent indexing entirely.
 */
export default function SEO({ title, description, path, image, type = "website", jsonLd = [], noindex = false }) {
  useEffect(() => {
    const canonical = `${CANONICAL_ORIGIN}${path || "/"}`;
    const imgAbs = toAbsolute(image);

    if (title) document.title = title;
    if (description) setMetaByName("description", description);

    setMetaByProperty("og:type", type);
    setMetaByProperty("og:url", canonical);
    if (title) setMetaByProperty("og:title", title);
    if (description) setMetaByProperty("og:description", description);
    setMetaByProperty("og:image", imgAbs);
    setMetaByProperty("og:locale", "es_ES");

    if (title) setMetaByName("twitter:title", title);
    if (description) setMetaByName("twitter:description", description);
    setMetaByName("twitter:image", imgAbs);

    if (noindex) {
      // Hidden/internal page: block indexing entirely.
      setMetaByName("robots", "noindex, nofollow, noarchive");
      setMetaByName("googlebot", "noindex, nofollow");
      removeCanonical();
      clearPageJsonLd();
    } else {
      setMetaByName("robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
      setMetaByName("googlebot", "index, follow");
      setCanonical(canonical);
      if (jsonLd && jsonLd.length) injectJsonLd(jsonLd);
      else clearPageJsonLd();
    }
    // Note: we do NOT clean up meta/canonical on unmount — the next mounted page
    // (or the persistent SITE-level meta) will overwrite them. This avoids flashes.
  }, [title, description, path, image, type, noindex, JSON.stringify(jsonLd)]);

  return null;
}

// Small helper to build breadcrumbs quickly.
export function buildBreadcrumb(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${CANONICAL_ORIGIN}${it.path}`,
    })),
  };
}

export { CANONICAL_ORIGIN, toAbsolute, SITE_ORIGIN };
